import React from 'react';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  Briefcase,
  Building2,
  Crown,
  PenLine,
  Target,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { fadeUp, displaySection, kicker, sectionWrap, sectionInner } from './shared';

/**
 * PersonasSection — 5 persona cards laid out as a responsive grid.
 * Each card frames the persona as **Challenge** + **Aspiration / Goal**
 * so visitors immediately see themselves and the outcome we help unlock.
 */

const PERSONAS = [
  {
    id: 'mba',
    Icon: GraduationCap,
    label: 'Final-Year MBA',
    challenge:
      'You\u2019re entering a market where being findable matters as much as being qualified.',
    aspiration:
      'Show up to recruiters before the interview \u2014 with a profile and posts that read like a hire.',
    fixes: [
      'Target-role headline + voice audit',
      'Researched posts on your thesis area',
      'Recruiter-ready About + content trail',
    ],
    tint: 'hsl(212 100% 97%)',
    avatar:
      'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=facearea&facepad=2.5&w=320&h=320&q=80',
    name: 'Karthik V.',
    fallback: 'KV',
  },
  {
    id: 'mid',
    Icon: Briefcase,
    label: 'Mid-Level Pro',
    challenge:
      'You have the experience \u2014 your LinkedIn doesn\u2019t make it obvious.',
    aspiration:
      'Be considered ready for the next level \u2014 before you ask.',
    fixes: [
      'Voice-matched weekly posts',
      'PoV drafts on your domain news',
      'Premium carousels of your real work',
    ],
    tint: 'hsl(150 60% 96%)',
    avatar:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2.5&w=320&h=320&q=80',
    name: 'Meera S.',
    fallback: 'MS',
  },
  {
    id: 'founder',
    Icon: Building2,
    label: 'Founder / SMB',
    challenge:
      'Your prospects audit you on LinkedIn before a discovery call \u2014 silently.',
    aspiration:
      'Close 20% of the deal before the call \u2014 with a profile that warms the room.',
    fixes: [
      'Buyer-side researched posts',
      'PoV on trending industry stories',
      'Inspiration benchmarks vs top voices',
    ],
    tint: 'hsl(38 92% 96%)',
    avatar:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=facearea&facepad=2.5&w=320&h=320&q=80',
    name: 'Rohan K.',
    fallback: 'RK',
  },
  {
    id: 'cxo',
    Icon: Crown,
    label: 'CXO / Senior Leader',
    challenge:
      'Your reputation is real offline. Your digital presence doesn\u2019t match the weight.',
    aspiration:
      'Let your LinkedIn match your real-world authority \u2014 without the hustle aesthetic.',
    fixes: [
      'Executive narrative + leadership themes',
      'Researched cadence \u2014 quiet, credible',
      'Profile Intelligence Framework built for your objective',
    ],
    tint: 'hsl(205 40% 97%)',
    avatar:
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=facearea&facepad=2.5&w=320&h=320&q=80',
    name: 'David L.',
    fallback: 'DL',
  },
];

export const PersonasSection = () => (
  <section className={sectionWrap} aria-label="Personas" id="personas">
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="mb-12 max-w-3xl">
        <p className={kicker}>Built for the moments that matter</p>
        <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
          Different goals.<br />
          <span className="text-primary">Same hidden gap.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Pick the lane closest to where you are today. Each persona has a
          different challenge — and a clear outcome we help unlock.
        </p>
      </motion.div>

      <div
        className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="persona-cards-grid"
      >
        {PERSONAS.map((p) => (
          <motion.div key={p.id} {...fadeUp}>
            <PersonaCard p={p} />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const PersonaCard = ({ p }) => (
  <Card
    className="group h-full rounded-3xl border border-border/70 bg-card/85 backdrop-blur hover:-translate-y-1 hover:shadow-[0_30px_70px_-40px_hsl(220_40%_2%/0.35)] transition-[transform,box-shadow] duration-300 overflow-hidden"
    data-testid={`persona-card-${p.id}`}
  >
    <CardContent className="p-7 sm:p-8 flex flex-col h-full">
      {/* header: avatar + label */}
      <div className="flex items-center gap-3">
        <Avatar className="h-12 w-12 ring-2 ring-background shadow-sm shrink-0">
          <AvatarImage
            src={p.avatar}
            alt={`${p.label} representative portrait`}
            className="object-cover"
            loading="lazy"
          />
          <AvatarFallback className="bg-secondary text-foreground text-[11px] font-medium">
            {p.fallback}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
            <p.Icon size={12} className="text-primary" aria-hidden="true" /> {p.label}
          </div>
          <p className="mt-1 text-sm text-foreground font-medium truncate">{p.name}</p>
        </div>
      </div>

      {/* challenge */}
      <div className="mt-5">
        <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
          Challenge
        </p>
        <p className="mt-2 text-[15px] font-semibold text-foreground tracking-[-0.01em] leading-snug">
          {p.challenge}
        </p>
      </div>

      {/* aspiration / outcome / goal */}
      <div
        className="mt-4 rounded-2xl border border-primary/20 p-4"
        style={{ background: p.tint }}
      >
        <p className="inline-flex items-center gap-1.5 text-[10px] font-mono-edge tracking-[0.22em] uppercase text-primary">
          <Target size={11} aria-hidden="true" /> Aspiration
        </p>
        <p className="mt-1.5 text-[14px] leading-snug text-foreground/90 italic">
          {p.aspiration}
        </p>
      </div>

      {/* fixes */}
      <div className="mt-5 pt-5 border-t border-dashed border-border/70">
        <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
          What we fix first
        </p>
        <ul className="mt-3 space-y-2">
          {p.fixes.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[13.5px]">
              <div className="mt-0.5 w-5 h-5 rounded-md bg-[hsl(var(--ep-accent-soft,212_100%_96%))] grid place-items-center text-primary shrink-0">
                <PenLine size={11} aria-hidden="true" />
              </div>
              <span className="text-foreground/90 leading-snug">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </CardContent>
  </Card>
);
